import type { ContentApprovalDTO, ContentDiffDTO, ContentVersionDTO } from '@today/types'

/** Approval track and diff presentation (TO-420 content-admin-ui REQ-007). */

type Op = ContentDiffDTO['body'][number]

export interface InlineRow {
	marker: '+' | '-' | ' '
	line: string
	op: Op['op']
}

export interface SideRow {
	left: string | null
	right: string | null
	op: Op['op']
}

/** "You" for the viewer, else the first 8 characters of the user id ('' for a system row). */
export const actorLabel = (userId: string | null, viewerId: string): { you: boolean; short: string } =>
	userId !== null && userId === viewerId ? { you: true, short: '' } : { you: false, short: (userId ?? '').slice(0, 8) }

/** The first 12 characters of a content hash. */
export const hashPrefix = (sha: string): string => sha.slice(0, 12)

const MARKER: Record<Op['op'], InlineRow['marker']> = { equal: ' ', delete: '-', insert: '+' }

/** Inline layout: every line with its `+` / `-` / space marker. */
export const inlineRows = (body: readonly Op[]): InlineRow[] =>
	body.map((o) => ({ marker: MARKER[o.op], line: o.line, op: o.op }))

/** Side by side: deletions left, insertions right, equal lines on both sides. */
export const sideBySideRows = (body: readonly Op[]): SideRow[] =>
	body.map((o) => ({
		left: o.op === 'insert' ? null : o.line,
		right: o.op === 'delete' ? null : o.line,
		op: o.op,
	}))

/** The versions (newest first, as listed) that hold a localization in `locale`. */
export const comparableVersions = (
	versions: readonly ContentVersionDTO[],
	locale: string,
): { versionNumber: number; localizationId: string }[] =>
	versions.flatMap((v) => {
		const loc = v.localizations.find((l) => l.locale === locale)
		return loc ? [{ versionNumber: v.versionNumber, localizationId: loc.id }] : []
	})

/** Timeline entries of an approval track, dates as `YYYY-MM-DD` (UTC). */
export const timelineEntries = (track: readonly ContentApprovalDTO[], viewerId: string) =>
	track.map((row) => {
		const actor = actorLabel(row.userId, viewerId)
		return {
			id: row.id,
			action: row.action,
			fromStatus: row.fromStatus,
			toStatus: row.toStatus,
			you: actor.you,
			actorShort: actor.short,
			actorRole: row.actorRole,
			reason: row.reason,
			validFrom: row.validFrom?.slice(0, 10) ?? null,
			validTo: row.validTo?.slice(0, 10) ?? null,
			hash: hashPrefix(row.contentSha256),
			date: new Date(row.createdAt).toISOString().slice(0, 10),
		}
	})
